// hooks, navigation, auth
import { useAuth } from 'auth/Auth';
import { useNavigate } from 'react-router-dom';
import React, {useState, useEffect, useMemo} from "react";

// Material UI
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ConfirmDeleteModal from "components/ConfirmDeleteModal";

// Material UI Styles
import Grid from "@mui/material/Grid";

// My modules
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Icon from "@mui/material/Icon";
import DataTable from "examples/Tables/DataTable";
import StatusBadge from "components/StatusBadge";
import MonoText from "components/MonoText";
import TableSkeleton from "components/CustomSkeleton/TableSkeleton";
import TableActionMenu from 'components/TableActionMenu';


const Trips = () => {

    const { axiosInstance } = useAuth();
    const navigate = useNavigate();

    // Initializing states

    const [trips, setTrips] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });

    // Trigger GET request on first render

    useEffect(() => {
        fetchTrips();
    }, []);

    // GET request to fetch trips data.

    const fetchTrips = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/trips');
            const sorted = [...(response.data || [])].sort((a, b) => {
                const aTime = new Date(a.updated_at || a.created_at || 0).getTime();
                const bTime = new Date(b.updated_at || b.created_at || 0).getTime();
                return bTime - aTime;
            });
            setTrips(sorted);
        } catch (error) {
            console.error('Error fetching trips:', error);
            setSnackbar({ open: true, message: 'Error loading trips', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    // Open detailed view page on row click

    const handleRowClick = (rowData) => {
        navigate(`/trips/detail/${rowData.id}`);
    };

    const handleEditClick = (rowData) => {
        navigate(`/trips/${rowData.id}/edit`);
    };

    // Update delete state when delete button is clicked.

    const handleDeleteClick = (id) => {
        setDeleteConfirm({ open: true, id });
    };

    // Handle delete operation for the row.

    const handleDelete = async () => {
        try {
            await axiosInstance.delete(`/trips/${deleteConfirm.id}`);
            setSnackbar({ open: true, message: 'Trip deleted successfully', severity: 'success' });
            fetchTrips();
        } catch (error) {
            console.error('Error deleting trip:', error);
            const detail = error.response?.data?.detail;
            const message = error.response?.data?.message;
            const errorMessage =
                (typeof detail === 'string' && detail)
                || (typeof message === 'string' && message)
                || error.message
                || 'Unable to delete trip destination.';
            setSnackbar({ open: true, message: errorMessage, severity: 'error' });
            throw error;
        }
    }

    const handleCloseDeleteModal = () => setDeleteConfirm({ open: false, id: null });


    const tableData = useMemo(() => ({
        columns: [
            {
                Header: "Country Name",
                accessor: "primary_country",
                width: "18%",
                Cell: ({ row }) => (
                    <MDTypography
                        component="span"
                        onClick={() => handleRowClick(row.original)}
                        variant="caption"
                        color="info"
                        fontWeight="medium"
                        sx={{
                            cursor: "pointer",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            overflowWrap: "anywhere",
                            lineHeight: 1.35,
                            display: "inline-block",
                            maxWidth: "100%",
                        }}
                    >
                        {row.original.primary_country}
                    </MDTypography>
                ),
            },
            {
                Header: "Continent",
                accessor: "continent",
                width: "14%",
                Cell: ({ value }) => (
                    <MDTypography
                        variant="caption"
                        sx={{
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            overflowWrap: "anywhere",
                            lineHeight: 1.35,
                        }}
                    >
                        {value || "-"}
                    </MDTypography>
                ),
            },
            {
                Header: "Packages",
                accessor: "package_count",
                width: "10%",
                align: "center",
                Cell: ({ value }) => value ?? 0,
            },
            {
                Header: "Status",
                accessor: "status",
                width: "14%",
                Cell: ({ value }) => <StatusBadge status={value || "draft"} />,
            },
            {
                Header: "Created",
                accessor: "created_at",
                width: "12%",
                Cell: ({ value }) => (
                    <MonoText>
                        {value ? new Date(value).toLocaleDateString() : "-"}
                    </MonoText>
                ),
            },
            {
                Header: "Modified",
                accessor: "updated_at",
                width: "12%",
                Cell: ({ value }) => (
                    <MonoText>
                        {value ? new Date(value).toLocaleDateString() : "-"}
                    </MonoText>
                ),
            },
            {
                Header: "Actions",
                accessor: "actions",
                width: "8%",
                align: "center",
                disableSortBy: true,
                Cell: ({ row }) => (
                    <TableActionMenu
                        row={row.original}
                        onView={handleRowClick}
                        onEdit={handleEditClick}
                        onDelete={handleDeleteClick}
                    />
                ),
            },
        ],
        rows: trips,
    }), [trips]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                    <Grid container spacing={6}>
                        <Grid item xs={12}>
                            <Card>
                                <MDBox
                                    mx={2}
                                    mt={-3}
                                    py={2}
                                    px={2}
                                    variant="gradient"
                                    bgColor="info"
                                    borderRadius="lg"
                                    coloredShadow="info"
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <MDBox>
                                        <MDTypography variant="h6" color="white">
                                            Travel Destinations
                                        </MDTypography>
                                        <MDTypography variant="caption" color="white" opacity={0.86}>
                                            Manage destination and country information for travel packages.
                                        </MDTypography>
                                    </MDBox>
                                    <MDButton variant="gradient" color="dark" onClick={() => navigate('/trips/new')}>
                                        <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                        &nbsp;Create Destination
                                    </MDButton>
                                </MDBox>
                                <MDBox pt={3}>
                                    {isLoading ? (
                                        <TableSkeleton />
                                    ) : (
                                        <DataTable
                                            table={tableData}
                                            isSorted
                                            noEndBorder
                                            canSearch
                                            exportCSV
                                            entriesPerPage={{
                                                defaultValue: 10,
                                                entries: [5, 10, 15, 20, 25],
                                            }}
                                            showTotalEntries
                                            pagination={{ variant: "gradient", color: "info" }}
                                            objectType="trip"
                                            onFilterClear={fetchTrips}
                                        />
                                    )}
                                </MDBox>
                            </Card>
                        </Grid>
                    </Grid>
                </MDBox>

                {/* Snackbar : Info, Error messages */}

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert
                        severity={snackbar.severity}
                        onClose={() => setSnackbar({ ...snackbar, open: false })}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>

                <ConfirmDeleteModal
                    open={deleteConfirm.open}
                    onClose={handleCloseDeleteModal}
                    onConfirm={handleDelete}
                    title="Delete Trip"
                    message="Are you sure you want to delete this trip? This action cannot be undone."
                />

        </DashboardLayout>
    )
}

export default Trips;
